import * as Sentry from '@sentry/react';
import { useAtomValue } from 'jotai';
import { useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { createEventReservationBooking } from '@/lib/api.ts';
import { IDV_REQUIRED_COPY } from '@/lib/apiErrorCode.ts';
import { track } from '@/lib/analytics/index.ts';
import { AuthAtom, useRestaurantId, useWidgetState } from '../lib/state.ts';
import { getActiveEventId, getSelectedTicketId } from '../lib/eventFlow.ts';
import { useBookingPreview, type BookingPreview } from '../lib/hooks/useBookingPreview.ts';
import type { WidgetState } from '../lib/types.ts';
import styles from '../EventPage/EventPage.module.css';
import { CheckoutSkeleton } from './CheckoutSkeleton.tsx';

type PreviewState = ReturnType<typeof useBookingPreview>;

function checkoutSelection(widget: WidgetState) {
  const ticketId = getSelectedTicketId(widget);
  return {
    items: [
      ...(ticketId
        ? Array.from({ length: Math.max(widget.eventInfo?.quantity ?? 1, 1) }, () => ticketId)
        : []),
      ...(widget.eventInfo?.addOnItemIds ?? []),
    ],
    hosted_club_checkout: true,
    use_credits: false,
    skip_level_check: true,
    opt_in_accepted: widget.opt_in_accepted,
    ...(widget.referral_code && { referral_code: widget.referral_code }),
    ...(widget.alternative_pickup_first_name &&
      widget.alternative_pickup_phone &&
      widget.alternative_pickup_email && {
        alternative_pickup_name:
          `${widget.alternative_pickup_first_name} ${widget.alternative_pickup_last_name ?? ''}`.trim(),
        alternative_pickup_phone: widget.alternative_pickup_phone,
        alternative_pickup_email: widget.alternative_pickup_email,
      }),
  };
}

/** Resolve the payment rail before mounting either page's Dorsia wallet hooks. */
export function EventCheckoutGate({
  children,
}: {
  children: (preview: PreviewState) => ReactNode;
}) {
  const [widget] = useWidgetState();
  const auth = useAtomValue(AuthAtom);
  const restaurantId = useRestaurantId();
  const fingerprint = JSON.stringify([
    auth?.user,
    restaurantId,
    getActiveEventId(widget),
    checkoutSelection(widget),
  ]);
  const pending =
    widget.bookingType === 'event' && widget.club_checkout?.fingerprint === fingerprint
      ? widget.club_checkout
      : undefined;
  // A previous request may already hold the last tickets. Replay its exact quote
  // and key before asking availability again, including after a lost response.
  const state = useBookingPreview(!pending);

  if (widget.bookingType !== 'event' || !auth?.token) return children(state);
  if (pending)
    return <ClubCheckout preview={state.preview} pending={pending} fingerprint={fingerprint} />;
  if (state.loading) return <CheckoutSkeleton />;
  if (state.error || !state.preview) {
    return (
      <div className={styles.card}>
        <p role="alert">
          {state.errorCode === 'IDV_REQUIRED'
            ? IDV_REQUIRED_COPY
            : (state.error ?? 'Please select your tickets again.')}
        </p>
        <Link to="../booking">Back to tickets</Link>
      </div>
    );
  }
  if (state.preview.requires_pmc_checkout)
    return <ClubCheckout preview={state.preview} fingerprint={fingerprint} />;
  return children(state);
}

function ClubCheckout({
  preview,
  pending,
  fingerprint,
}: {
  preview: BookingPreview | null;
  pending?: WidgetState['club_checkout'];
  fingerprint: string;
}) {
  const auth = useAtomValue(AuthAtom);
  const [widget, updateWidget] = useWidgetState();
  const restaurantId = useRestaurantId();
  const submitting = useRef(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function continueToPayment() {
    if (submitting.current || !auth?.token) return;
    const eventId = getActiveEventId(widget);
    const ticketId = getSelectedTicketId(widget);
    if (!restaurantId || !eventId || !ticketId || (!pending && !preview)) {
      setError('Please go back and select your tickets again.');
      return;
    }
    submitting.current = true;
    setLoading(true);
    setError(null);
    track('Widget Book Pressed', { restaurant_id: restaurantId, booking_type: 'event' });
    try {
      const intent =
        pending ??
        (preview
          ? {
              fingerprint,
              key: crypto.randomUUID(),
              body: { ...checkoutSelection(widget), confirmed_total: preview.total_paid },
              restaurantName: preview.restaurant.name,
              totalLabel: preview.total_paid__currency,
            }
          : null);
      if (!intent) throw new Error('Missing checkout preview');
      const { body, key } = intent;
      updateWidget({ club_checkout: intent });
      const response = await createEventReservationBooking(
        auth.token,
        { ...body, idempotency_key: key },
        eventId,
        restaurantId
      );
      if (!response.checkout_url || !response.reservation_uuid)
        throw new Error('Missing club checkout URL');
      const url = new URL(response.checkout_url);
      if (url.protocol !== 'https:' || url.username || url.password)
        throw new Error('Invalid club checkout URL');
      // The API resolves the club's canonical domain. Payment and confirmation happen there.
      window.location.assign(url.href);
    } catch (cause) {
      Sentry.captureException(cause);
      track('Widget Book Failed', {
        restaurant_id: restaurantId,
        booking_type: 'event',
        error_code: 'club_checkout_handoff_failed',
      });
      setError('Unable to open payment. Please try again, or go back to select your tickets.');
      submitting.current = false;
      setLoading(false);
    }
  }

  return (
    <div className={styles.twoColumnLayout}>
      <div className={styles.mainColumn}>
        <div className={styles.card}>
          <div className={styles.formSection}>
            <h2>Complete your booking</h2>
            <p>
              Continue to {pending?.restaurantName ?? preview?.restaurant.name}’s secure checkout to
              review your order and pay.
            </p>
            {preview?.items.map((item, index) => (
              <p key={index}>
                {item.name} × {item.quantity ?? 1}
              </p>
            ))}
            <p>Total: {pending?.totalLabel ?? preview?.total_paid__currency}</p>
            {error && (
              <p role="alert" className={styles.error}>
                {error}
              </p>
            )}
            <button
              type="button"
              className={styles.primaryButton}
              disabled={loading}
              onClick={() => void continueToPayment()}
            >
              {loading ? 'Opening payment…' : 'Continue to payment'}
            </button>
            <Link to="../booking" onClick={() => updateWidget({ club_checkout: undefined })}>
              Back to tickets
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
